import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from 'react-oidc-context';
import { toast } from 'react-toastify';
import { summarizeFile } from '../../../../api/upload';
import { FileInput } from '../../../../components/FileInput/FileInput';
import {
  Tooltip,
  type TooltipHandle,
} from '../../../../components/Tooltip/Tooltip';
import { useSessionFeatureState } from '../../../../hooks/storeHooks/useSessionState';
import { OidcUser } from '../../../../types/oidcUser';
import { uploadFile } from '../../../../utils/fileUploader';
import { getOrganizationId } from '../../../../utils/getOrganization';

interface IProps {
  avatar_id: string;
  mentorId: string;
}

export const UploadInput: React.FC<IProps> = ({ mentorId }) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { setIsFileUploading, isFileUploading, socketId } =
    useSessionFeatureState();
  const tooltipRef = useRef<TooltipHandle>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isProcessing, setIsProcessing] = useState(false);

  const [file, setFile] = useState<File | null>(null);

  const orgId = getOrganizationId(user as OidcUser);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { files } = event.target;

    if (files && files.length > 0) {
      const newFile = files[0];
      const MAX_FILE_SIZE = 20 * 1024 * 1024; // 20MB
      const allowedTypes = ['image/png', 'image/jpeg', 'application/pdf'];
      if (newFile.size > MAX_FILE_SIZE) {
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        toast.warning(t('avatar.fileSizeLimit'));
        setIsFileUploading(false);
        return;
      }
      if (!allowedTypes.includes(newFile.type)) {
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        toast.warning(t('avatar.invalidFileType'));
        setIsFileUploading(false);
        return;
      }
      setFile(newFile);
      return;
    }
    setFile(null);
  };

  useEffect(() => {
    if (!isFileUploading && isProcessing) {
      setIsProcessing(false);
    }
  }, [isFileUploading, isProcessing]);

  // TODO: DRY this up with PiPControls.tsx
  const uploadAndSummarizeFile = async (newFile: File) => {
    try {
      setIsFileUploading(true);
      const response = await uploadFile(newFile, orgId);
      if (response?.id) {
        setIsProcessing(true);
        const resp = await summarizeFile({
          mentorId,
          uploadId: response.id,
          socketId: socketId || '',
        });
        if (!resp.success) {
          throw new Error('Failed to summarize file');
        }
      }
    } catch (error) {
      console.error('Error uploading file:', error);
      toast.error(t('avatar.errorUploading'));
    } finally {
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      setIsProcessing(false);
      setIsFileUploading(false);
    }
  };

  useEffect(() => {
    if (file != null) {
      uploadAndSummarizeFile(file).catch((error) => {
        console.error('Error in file upload:', error);
      });
    }
  }, [file]);

  const handleTouchEnd = () => {
    tooltipRef.current?.closeWithDelay();
  };

  const tooltipText = isProcessing
    ? t('avatar.processing')
    : isFileUploading
    ? t('avatar.uploading')
    : t('avatar.uploadFile');

  return (
    <>
      <Tooltip content={tooltipText} anchor="#upload-file" />
      <div id="upload-file" onTouchEnd={handleTouchEnd}>
        <FileInput
          isFileUploading={isFileUploading}
          handleFileChange={handleFileChange}
          ref={fileInputRef}
        />
      </div>
    </>
  );
};
